import httpStatusDefinitions from "@/data/httpStatusDefinitions";
import EventBus from "@/utilities/eventBus";

class APIError extends Error {
  constructor(message, summary, sourceError) {
    super(message);
    this.summary = summary;
    this.sourceError = sourceError;
    this.name = 'APIError';
  }
}

class FieldError extends APIError {
  constructor(message, summary, sourceError, fieldErrors) {
    super(message, summary, sourceError);
    this.fieldErrors = fieldErrors;
    this.name = 'FieldError';
  }
}

class CancelError extends APIError {
  constructor(message, summary, sourceError) {
    super(message, summary, sourceError);
    this.name = 'CancelError'
  }
}

/*
  Method accepts an error from Axios as argument and runs logic
  to transform the error before returning it to the calling code.

  In particular, the method looks for .response on the provided error and determines
  if the backend returned a basic API error, or a list of global errors or field errors.

  If the error contains field errors, the method returns a FieldError object with the
  list of field errors in a .fieldErrors property. It is up to the developer to understand
  when an endpoint might return field errors and handle them appropriately in the calling code.

  Otherwise, the method returns an APIError object with a specific error message
  provided by the backend or a fallback error message provided by the calling code.
  The following logic determines when the fallback error message is returned:
    - The message returned by the backend is the generic 'there was an error'
    - The global errors array contains more than one global error
    - The error represents a list of field errors (individual field errors are returned in addition to the message)
    - No response is received

  The method accepts a handleHere argument, which will trigger a toast message in App.vue via EventBus when true.
  The variable is set to false by default, so the developer must explicitly opt into this 'global error handling' solution.
  
  Note: rejectOnAxiosError always returns an error object, so some form of .catch logic is required in
  the calling code to prevent 'uncaught (in promise) errors' from appearing in the browser console.

  Finally, after determining the correct error message, the method calls console.error with that message and the sourceError.
*/
function rejectOnAxiosError(sourceError, fallbackErrorMessage, handleHere = false, preferGlobalErrorsOverFieldErrors) {
  const appendPeriod = (message) => {
    const endsWithPeriod =  message[message.length - 1] === '.';
    
    if (endsWithPeriod) return message;
    else return message + '.';
  }

  let error;
  const summary = (() => {
    if (sourceError.response) {
      const { status } = sourceError.response;
      const statusDef = httpStatusDefinitions
        .find(({ code }) => code === status);

      return `${statusDef.code} ${statusDef.reason}`;
    } else if (sourceError.request) {
      return 'Server did not respond'
    } else return 'Request setup failed'
  })();

  if (sourceError.response) {
    const { data: backendResponse } = sourceError.response;

    const responseKeys = Object.keys(backendResponse);

    // Evaluate if response represents basic API error
    const isAPIError = (
      responseKeys.includes('status') &&
      responseKeys.includes('message') &&
      responseKeys.includes('details')
    );

    // Handle basic API error
    if (isAPIError) {
      const { message } = backendResponse;
      if (!message?.toLowerCase().includes("there was an error")) {
        error = new APIError(message, summary, sourceError);
      } else error = new APIError(fallbackErrorMessage, summary, sourceError)
    }
    
    const handleGlobalErrors = (res) => {
      const isGlobalError = (
        responseKeys.includes('errors') &&
        res.errors.globalErrors &&
        res.errors.globalErrorsCount > 0
      )

      if (isGlobalError) {
        const globalErrors = res.errors.globalErrors;
        if (res.errors.globalErrorsCount === 1) {
          const { defaultMessage } = globalErrors[0];
          error = new APIError(defaultMessage, summary, sourceError);
        } else error = new APIError(fallbackErrorMessage, summary, sourceError);
      }
    }

    const handleFieldErrors = (res) => {
      const isFieldError = (
        responseKeys.includes('errors') &&
        res.errors.fieldErrors &&
        res.errors.fieldErrorsCount > 0
      )

      if (isFieldError) {
        const fieldErrors = res.errors.fieldErrors;
        error = new FieldError(fallbackErrorMessage, summary, sourceError, fieldErrors);
      }
    }

    let specialErrorHandlers = [handleGlobalErrors, handleFieldErrors];
    if (preferGlobalErrorsOverFieldErrors) specialErrorHandlers = specialErrorHandlers.reverse();

    specialErrorHandlers.forEach((f) => f(backendResponse));
  }

  // Handle other error types: unknown errors in response, canceled requests, no response and failed request setup
  if (!error) {
    if (sourceError.code === 'ERR_CANCELED') {
      error = new CancelError('The request was canceled.', summary, sourceError);
    } else error = new APIError(fallbackErrorMessage, summary, sourceError);
  }
  
  // Backend tends to return error messages w/o periods.
  error.message = appendPeriod(error.message);
  if (error.fieldErrors) error.fieldErrors = error.fieldErrors
    .map((fieldError) => ({ ...fieldError, defaultMessage: appendPeriod(fieldError.defaultMessage)}))

  if (handleHere) EventBus.emit('add-toast', { severity: 'error', args: [error.message, error.summary] })
  if (!(error instanceof CancelError)) {
    console.error(`${error.message} See details below: %o`, sourceError.response ?? sourceError.request ?? sourceError.message);
  }

  return Promise.reject(error);
}

export { APIError, FieldError, CancelError, rejectOnAxiosError };